import React, { useState } from 'react';
import { ReactComponent as RejectRiderIcon } from 'assets/icons/reject-rider-icon.svg';
import { toast } from 'react-toastify';
import {
  BaseModal,
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  Input,
  Textarea,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { ArrowDownIcon } from 'cokitchen-icons';
import { apiService } from 'utils/api';
import { ExternalRiderType } from 'models/external-rider.model';
import { ENUM_AUTHORIZE_TYPE } from 'utils/enum';
import { otherReasons, rejectRiderReasons } from 'pages/external-rider/utils';

type Props = {
  show: boolean;
  onClose: () => void;
  riders: ExternalRiderType[];
  onSuccess: () => void;
};

type FooterProps = {
  onCancel: () => void;
  loading: boolean;
  onConfirm: () => void;
};

const ExternalRiderFooter = ({ onCancel, onConfirm, loading }: FooterProps) => (
  <div className='grid grid-cols-2 gap-x-4'>
    <Button small color='primary' variant='outline' className='mx-auto' onClick={onCancel}>
      Cancel
    </Button>
    <Button
      small
      color='primary'
      variant='block'
      className='mx-auto'
      loading={loading}
      onClick={onConfirm}>
      Confirm
    </Button>
  </div>
);

const RejectExternalRiderModal = ({ show, onClose, riders, onSuccess }: Props) => {
  const [reason, setReason] = useState<string[]>([]);
  const [comment, setComment] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleCheckBox = (reas: string) => {
    const findReason = reason.filter((r) => r === reas)?.[0];
    setReason(findReason ? reason.filter((r) => r !== reas) : [...reason, reas]);
  };

  const handleRejectApplication = async () => {
    try {
      setLoading(true);
      await apiService.post('rider-applications/bulk', {
        data: {
          reason_types: reason,
          comments: comment,
          application_ids: riders.map((rider) => rider.application_id),
          action: ENUM_AUTHORIZE_TYPE.REJECT
        },
        baseUrl: process.env.REACT_APP_AUTH_API_URL
      });
      onSuccess();
      toast(<ToastMessage type='success' message='Application(s) rejected successfully' />);
      onClose();
    } catch (err: any) {
      toast(<ToastMessage type='error' message={'An error ocecured'} />);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal
      isOpen={show}
      title={'Reject Rider(s)'}
      description={'Any reason why you are rejecting this rider(s)?'}
      titleIcon={<RejectRiderIcon />}
      titleIconColor='danger'
      onRequestClose={() => onClose()}
      footer={
        <ExternalRiderFooter
          loading={loading}
          onCancel={() => onClose()}
          onConfirm={handleRejectApplication}
        />
      }>
      <>
        <div className='mr-16'>
          <div className='grid gap-x-5 gap-y-5 md:gap-y-3 md:grid-cols-2 grid-rows-2 my-4'>
            {rejectRiderReasons.map((rea, i) => (
              <Input
                key={i}
                value={rea}
                label={rea.replaceAll('_', ' ')}
                className='w-full capitalize text-sm'
                type='checkbox'
                onChange={(e) => handleCheckBox(e.target.value)}
              />
            ))}
          </div>

          <Dropdown
            className='left-0 relative'
            content={
              <button className='flex items-center gap-2 group text-sm font-medium text-gray-6/80'>
                Others
                <ArrowDownIcon className='group-hover:rotate-180 ' />
              </button>
            }>
            <DropdownList className='min-w-[400px] bg-transparent shadow-none'>
              <div className='bg-white w-fit shadow border-gray-1 border rounded-lg overflow-scroll overscroll-y-contain'>
                {otherReasons.map((reas, i) => (
                  <DropdownItem key={i} className='list-none'>
                    <Input
                      className='text-sm transition-all duration-200 capitalize'
                      type='checkbox'
                      label={reas.replaceAll('_', ' ')}
                      value={reas}
                      onChange={(e) => handleCheckBox(e.target.value)}
                    />
                  </DropdownItem>
                ))}
              </div>
            </DropdownList>
          </Dropdown>
        </div>

        <div className='mt-2'>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder='Add a comment...'
            className='w-full min-h-32'
            maxLength={150}
          />
          <span className='text-xs font-semibold mt-1 text-gray-5'>
            Word limit: {comment.length}/150
          </span>
        </div>
      </>
    </BaseModal>
  );
};

export default RejectExternalRiderModal;
